import { cssVarOf, type BridgeColumns, type MetricKey, METRIC_FULL_FORMAT } from '@integrate/core';

/*
  A waterfall: subtotal bars from zero, step bars floating at the running total.

  The arithmetic is entirely in core (`phaseBridgeCols`, `remainMirrorCols`), where the parity harness
  compares every column's label, extent, value AND colour against the shipped app — colour included
  because the port names colours as tokens rather than repeating the CSS variables, so comparing them is
  what proves the token table maps back to the same palette. Nothing here decides anything: it scales
  and it draws.

  READABLE WITHOUT PIXELS. Each column carries `data-lo`, `data-hi` and `data-val`, and there is a table
  twin below, for the same reason the stranded chart has one: a bar chart whose only assertion is a
  screenshot is a chart nobody can test, and a reader using a screen reader gets nothing from it either.
*/

const HEIGHT = 150;

export function Waterfall({
  columns,
  metric,
  testId,
  caption,
}: {
  columns: BridgeColumns;
  metric: MetricKey;
  testId: string;
  /** Names what the twin table is a twin OF, since two of these can be on one pane. */
  caption: string;
}): JSX.Element {
  const format = METRIC_FULL_FORMAT[metric];
  // Guarded, because an empty project has every column at zero and this is a divisor.
  const scale = HEIGHT / Math.max(columns.maxBar, 1);

  const label = (c: BridgeColumns['cols'][number]): string => {
    if (c.total) return format(c.val);
    const size = Math.abs(c.val);
    // A dash, not "+0": a step that moves nobody is not an increase of nothing.
    return size ? `${c.val < 0 ? '−' : '+'}${format(size)}` : '–';
  };

  return (
    <div data-testid={testId} data-max-bar={columns.maxBar}>
      <div className="wf">
        {columns.cols.map((c, i) => (
          <div
            className="wf-col"
            /*
              Index-keyed on purpose. A waterfall's columns are a FIXED SEQUENCE from one function — the
              same eleven every time, in the same order — so the index is the identity here, and two
              columns legitimately share a label ("No longer req." appears in both bridges).
            */
            key={i}
            data-col={c.label}
            data-lo={c.lo}
            data-hi={c.hi}
            data-val={c.val}
            data-total={c.total ? 'true' : undefined}
          >
            <div className="wf-num">{label(c)}</div>
            <div className="wf-track" style={{ height: HEIGHT }}>
              <div
                className="wf-bar"
                style={{
                  // A floor of 2px so a step of one person is still visible beside a bar of 400.
                  height: Math.max(2, (c.hi - c.lo) * scale),
                  bottom: c.lo * scale,
                  background: cssVarOf(c.color),
                }}
              />
            </div>
            <div className="wf-lbl" title={c.label}>
              {c.label}
            </div>
          </div>
        ))}
      </div>
      <details className="chart-table">
        <summary>{caption} as a table</summary>
        <table className="data-table">
          <thead>
            <tr>
              <th>Step</th>
              <th className="num">Change</th>
              <th className="num">Running total</th>
            </tr>
          </thead>
          <tbody>
            {columns.cols.map((c, i) => (
              <tr key={i}>
                <td>{c.label}</td>
                <td className="num">{c.total ? '' : label(c)}</td>
                <td className="num">{format(c.hi)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
